import { getApplicableBadges } from "@/lib/badges";
import {
  getBadgeRule,
  isEntityNewByRule,
  type BadgeRuleRow,
} from "@/lib/badgeRules";
import type { OpenVenue, PulseEvent } from "@/lib/events";
import { isEventLiveFromPulse } from "@/lib/events";

export type TagFilterId = "live" | "top_pick" | "popular" | "new" | "suggested";

export const TAG_OPTIONS: { id: TagFilterId; label: string }[] = [
  { id: "live", label: "🔴 Live" },
  { id: "top_pick", label: "⭐ Top Pick" },
  { id: "popular", label: "🔥 Popular" },
  { id: "new", label: "✨ New" },
  { id: "suggested", label: "🎯 Suggested" },
];

export function createDefaultTagFilters(): Set<TagFilterId> {
  return new Set();
}

export function isTagFilterActive(filters: Set<TagFilterId>, tag: TagFilterId): boolean {
  return filters.has(tag);
}

export function toggleTagFilter(filters: Set<TagFilterId>, tag: TagFilterId): Set<TagFilterId> {
  const next = new Set(filters);
  if (next.has(tag)) {
    next.delete(tag);
  } else {
    next.add(tag);
  }
  return next;
}

export function countActiveTagFilters(filters: Set<TagFilterId>): number {
  return filters.size;
}

export function computeEventTagFlags(
  event: PulseEvent,
  badgeRules?: BadgeRuleRow[],
): Pick<PulseEvent, "isTopPick" | "isPopular" | "isNew"> {
  const badges = getApplicableBadges(
    {
      live: event.live ?? false,
      popular: event.popular,
      isTopPick: event.isTopPick,
      savedCount: event.savedCount,
      ticketsSold: event.ticketsSold,
      capacity: event.capacity,
      createdAt: event.createdAt,
    },
    badgeRules,
  );

  return {
    isTopPick: badges.includes("topPick"),
    isPopular: badges.includes("popular"),
    isNew: badges.includes("new"),
  };
}

export function matchesEventTagFilter(
  event: PulseEvent,
  selectedTags: Set<TagFilterId>,
  suggestedEventIds: Set<string>,
): boolean {
  if (selectedTags.size === 0) return true;

  return (
    (selectedTags.has("live") && isEventLiveFromPulse(event)) ||
    (selectedTags.has("top_pick") && event.isTopPick) ||
    (selectedTags.has("popular") && event.isPopular) ||
    (selectedTags.has("new") && event.isNew) ||
    (selectedTags.has("suggested") && suggestedEventIds.has(event.id))
  );
}

function venueHasTopPick(venue: OpenVenue): boolean {
  return venue.isTopPick;
}

export function venueHasNew(venue: OpenVenue, badgeRules?: BadgeRuleRow[]): boolean {
  const rule = getBadgeRule(badgeRules, "new", "venue");
  return isEntityNewByRule(venue.createdAt, rule);
}

function venueHasPopular(venue: OpenVenue): boolean {
  return venue.isPopular;
}

/** Single highlight for discovery cards — Top Pick > Popular > New > vibe. */
export type VenuePrioritySlot =
  | { kind: "topPick" }
  | { kind: "popular" }
  | { kind: "new" }
  | { kind: "vibe"; vibe: NonNullable<OpenVenue["vibe"]> };

export function getVenuePrioritySlot(
  venue: OpenVenue,
  badgeRules?: BadgeRuleRow[],
): VenuePrioritySlot | null {
  if (venueHasTopPick(venue)) return { kind: "topPick" };
  if (venueHasPopular(venue)) return { kind: "popular" };
  if (venueHasNew(venue, badgeRules)) return { kind: "new" };
  if (venue.vibe) return { kind: "vibe", vibe: venue.vibe };
  return null;
}

export function matchesVenueTagFilter(
  venue: OpenVenue,
  selectedTags: Set<TagFilterId>,
  badgeRules?: BadgeRuleRow[],
): boolean {
  if (selectedTags.size === 0) return true;

  const venueTags = [...selectedTags].filter(
    (tag) => tag !== "suggested" && tag !== "live",
  );
  if (venueTags.length === 0) return false;

  return (
    (selectedTags.has("top_pick") && venueHasTopPick(venue)) ||
    (selectedTags.has("popular") && venueHasPopular(venue)) ||
    (selectedTags.has("new") && venueHasNew(venue, badgeRules))
  );
}
